<template>
    <div class="voice-agent-page-content">
        <!-- Bilgi Tabanları Listesi -->
        <div v-if="!currentKnowledgeBaseId">
            <div class="header-section">
                <div class="title-wrapper">
                    <h1 class="main-title">{{ $t("voiceagent.knowledgeBase") }}</h1>
                    <p class="title-description">{{ $t("voiceagent.knowledgeBaseDescription") }}</p>
                </div>
            </div>
            <div class="section">
                <div class="table-wrapper">
                    <div class="toolbar">
                        <div class="left">
                            <a-input-search
                                style="width: 300px; margin: unset !important"
                                v-model="searchText"
                                placeholder="Ara"
                                class="search-input"
                                @input="onSearch"
                            />
                        </div>
                        <div class="right">
                            <PermissionController :permissions='["voice-agent.create"]'>
                                <SupsisButton primary @click="createKnowledgeBase">
                                    <i class="material-icons">add</i>
                                    {{ $t("voiceagent.newKnowledgeBase") }}
                                </SupsisButton>
                            </PermissionController>
                        </div>
                    </div>

                    <a-spin :spinning="loading">
                        <SupsisTable
                            :columns="getCols()"
                            :rows="paginatedKnowledgeBases"
                            :top-pagination="true"
                            empty-data-bordered
                            :bottom-pagination="false"
                            :pagination-cursor="pagination"
                            :options="{
                                rowStyleOption,
                                clipboardOption,
                                fixedHeader: true,
                                id: 'knowledge-bases-table',
                                borderX: true,
                                borderY: true,
                            }"
                            @cursor-changed="(cursor) => (pagination = cursor)"
                        />
                    </a-spin>
                </div>
            </div>
        </div>

        <!-- Bilgi Tabanı Detayı -->
        <KnowledgeBaseDetail
            v-else
            :channelId="channelId"
            :knowledgeBaseId="currentKnowledgeBaseId"
            @back="goBackToList"
        />

        <!-- Yeni Bilgi Tabanı Oluşturma Modal -->
        <a-modal
            v-model="showCreateModal"
            :title="$t('voiceagent.createNewKnowledgeBase')"
            :footer="null"
            @cancel="showCreateModal = false"
            width="600px"
        >
            <a-form :form="form" layout="vertical" @submit="handleCreateSubmit">
                <a-form-item :label="$t('voiceagent.knowledgeBaseName')">
                    <a-input
                        v-decorator="[
                            'knowledge_base_name',
                            {
                                rules: [{ required: true, message: 'Lütfen bilgi tabanı adı girin!' }]
                            }
                        ]"
                        placeholder="Bilgi Tabanı Adı"
                    />
                </a-form-item>

                <a-form-item :label="$t('voiceagent.urlsPerLine')">
                    <a-textarea
                        v-decorator="['knowledge_base_urls']"
                        placeholder="https://www.example.com"
                        :rows="4"
                    />
                </a-form-item>

                <a-form-item :label="$t('voiceagent.uploadFile')">
                    <a-upload-dragger
                        name="files"
                        :multiple="true"
                        :fileList="createFileList"
                        @change="handleCreateFileChange"
                        :beforeUpload="beforeUpload"
                    >
                        <p class="ant-upload-drag-icon">
                            <a-icon type="inbox" />
                        </p>
                        <p class="ant-upload-text">{{ $t("voiceagent.dragDropOrClickToSelect") }}</p>
                        <p class="ant-upload-hint">
                            {{ $t("voiceagent.supportedFileTypes") }}
                        </p>
                    </a-upload-dragger>
                </a-form-item>

                <a-form-item>
                    <a-checkbox
                        v-decorator="['enable_auto_refresh', { valuePropName: 'checked' }]"
                    >
                        {{ $t("voiceagent.autoRefreshEvery12Hours") }}
                    </a-checkbox>
                </a-form-item>

                <a-form-item :label="$t('voiceagent.addText')">
                    <div v-for="(text, index) in texts" :key="index" class="text-item">
                        <a-row :gutter="16">
                            <a-col :span="8">
                                <a-input
                                    v-model="text.title"
                                    placeholder="Başlık"
                                    class="mb-2"
                                />
                            </a-col>
                            <a-col :span="14">
                                <a-textarea
                                    v-model="text.text"
                                    placeholder="Metin İçeriği"
                                    :rows="2"
                                    class="mb-2"
                                />
                            </a-col>
                            <a-col :span="2">
                                <a-button
                                    shape="circle"
                                    type="danger"
                                    icon="delete"
                                    @click="removeText(index)"
                                />
                            </a-col>
                        </a-row>
                    </div>
                    <a-button type="dashed" block @click="addText">
                        <i class="material-icons" style="font-size: 18px; vertical-align: middle;">add</i>
                        {{ $t("voiceagent.addText") }}
                    </a-button>
                </a-form-item>

                <div class="button-container">
                    <a-button class="mr-2" @click="showCreateModal = false">
                        {{ $t("voiceagent.cancel") }}
                    </a-button>
                    <a-button type="primary" html-type="submit" :loading="submitting">
                        {{ $t("voiceagent.create") }}
                    </a-button>
                </div>
            </a-form>
        </a-modal>
    </div>
</template>

<script>
import { VOICE_AGENT_API } from "../../../../../api/voiceAgent";
import SupsisTable from "../../../../../components/SupsisTable.vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import KnowledgeBaseDetail from "./KnowledgeBaseDetail.vue";
import _ from "lodash";
import moment from "moment";

export default {
    name: "VoiceAgentKnowledgeBase",
    components: {
        SupsisTable,
        SupsisButton,
        KnowledgeBaseDetail,
    },
    props: {
        channelId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            searchText: "",
            knowledgeBases: [],
            loading: false,
            submitting: false,
            pagination: {
                page: 1,
                perPage: 10,
                total: 0,
            },
            rowStyleOption: {
                stripe: true,
            },
            clipboardOption: {
                copy: true,
                paste: false,
                cut: false,
                delete: false,
            },
            showCreateModal: false,
            form: this.$form.createForm(this),
            texts: [],
            createFileList: [],
            // Bilgi tabanı detayı için gerekli değişkenler
            currentKnowledgeBaseId: this.$route.query.kb_id || null,
        };
    },
    computed: {
        filteredKnowledgeBases() {
            if (!this.searchText) {
                return this.knowledgeBases;
            }
            const searchLower = this.searchText.toLowerCase();
            return this.knowledgeBases.filter((kb) => {
                const name = _.get(kb, "knowledge_base_name", "").toLowerCase();
                return name.includes(searchLower);
            });
        },
        paginatedKnowledgeBases() {
            const start = (this.pagination.page - 1) * this.pagination.perPage;
            const end = start + this.pagination.perPage;
            return this.filteredKnowledgeBases.slice(start, end);
        },
    },
    watch: {
        pagination: {
            handler() {
                // Sayfa değişikliğinde yapılacak işlemler
            },
            deep: true,
        },
        "$route.query.kb_id"(newId) {
            // URL'deki kb_id değiştiğinde
            this.currentKnowledgeBaseId = newId || null;
        },
    },
    methods: {
        async fetchKnowledgeBases() {
            try {
                this.loading = true;
                const knowledgeBases = await VOICE_AGENT_API.knowledgeBases.list();
                this.knowledgeBases = knowledgeBases;
                this.pagination.total = knowledgeBases.length;
            } catch (error) {
                console.error("Bilgi tabanları çekilirken hata oluştu:", error);
                this.$message.error("Bilgi tabanları yüklenirken bir hata oluştu");
            } finally {
                this.loading = false;
            }
        },
        onSearch() {
            this.pagination.page = 1;
        },
        getCols() {
            return [
                {
                    key: "name",
                    field: "knowledge_base_name",
                    title: "İsim",
                    renderBodyCell: ({ row }) => {
                        return (
                            <a class="kb-name-link" onClick={() => this.viewKnowledgeBase(row.knowledge_base_id)}>
                                {_.get(row, "knowledge_base_name", "İsimsiz Bilgi Tabanı")}
                            </a>
                        );
                    },
                },
                {
                    key: "status",
                    field: "status",
                    title: "Durum",
                    renderBodyCell: ({ row }) => {
                        return (
                            <a-tag color={this.getStatusColor(row.status)}>
                                {this.getStatusText(row.status)}
                            </a-tag>
                        );
                    },
                },
                {
                    key: "sources",
                    field: "knowledge_base_sources",
                    title: "Kaynak Sayısı",
                    renderBodyCell: ({ row }) => {
                        const sources = _.get(row, "knowledge_base_sources", []);
                        return sources.length;
                    },
                },
                {
                    key: "autoRefresh",
                    field: "enable_auto_refresh",
                    title: "Otomatik Yenileme",
                    renderBodyCell: ({ row }) => {
                        return row.enable_auto_refresh ?
                            <a-tag color="green">Aktif</a-tag> :
                            <a-tag color="orange">Pasif</a-tag>;
                    },
                },
                {
                    key: "lastRefreshed",
                    field: "last_refreshed_timestamp",
                    title: "Son Yenileme",
                    renderBodyCell: ({ row }) => {
                        if (!row.last_refreshed_timestamp) return "-";
                        const date = new Date(row.last_refreshed_timestamp);
                        return moment(date).format("DD.MM.YYYY HH:mm");
                    },
                },
                {
                    key: "actions",
                    field: "actions",
                    title: "İşlemler",
                    renderBodyCell: ({ row }) => {
                        return (
                            <div class="actions">
                                <SupsisButton
                                    class="view-btn"
                                    onClick={() => this.viewKnowledgeBase(row.knowledge_base_id)}
                                    ghost
                                    icon="visibility"
                                    small
                                />
                                <SupsisButton
                                    onClick={() => this.deleteKnowledgeBase(row.knowledge_base_id)}
                                    danger
                                    ghost
                                    icon="delete"
                                    small
                                />
                            </div>
                        );
                    },
                },
            ];
        },
        getStatusColor(status) {
            if (status === "complete") return "green";
            if (status === "in_progress") return "blue";
            if (status === "error") return "red";
            return "default";
        },
        getStatusText(status) {
            if (status === "complete") return "Tamamlandı";
            if (status === "in_progress") return "İşleniyor";
            if (status === "error") return "Hata";
            return status;
        },
        createKnowledgeBase() {
            this.texts = [];
            this.showCreateModal = true;
        },
        viewKnowledgeBase(knowledgeBaseId) {
            // Bilgi tabanı ID'sini state'e ata ve URL'yi güncelle
            this.currentKnowledgeBaseId = knowledgeBaseId;

            // URL'yi güncelle ama sayfayı yenileme
            this.$router.push({
                query: { ...this.$route.query, kb_id: knowledgeBaseId },
            });
        },
        goBackToList() {
            // Bilgi tabanı ID'sini null yap ve URL'yi güncelle
            this.currentKnowledgeBaseId = null;

            // URL'den kb_id'yi kaldır
            const query = { ...this.$route.query };
            delete query.kb_id;
            this.$router.push({ query });
        },
        async deleteKnowledgeBase(knowledgeBaseId) {
            try {
                await this.$confirm({
                    title: "Bilgi Tabanını Sil",
                    content: "Bu bilgi tabanını silmek istediğinize emin misiniz?",
                    okText: "Evet",
                    okType: "danger",
                    cancelText: "Hayır",
                    onOk: async () => {
                        this.loading = true;
                        await VOICE_AGENT_API.knowledgeBases.delete(knowledgeBaseId);
                        this.knowledgeBases = this.knowledgeBases.filter(
                            (kb) => kb.knowledge_base_id !== knowledgeBaseId,
                        );
                        this.pagination.total = this.knowledgeBases.length;
                        this.loading = false;
                        this.$message.success("Bilgi tabanı başarıyla silindi");
                    },
                });
            } catch (error) {
                console.error("Bilgi tabanı silinirken hata oluştu:", error);
                this.$message.error("Bilgi tabanı silinirken bir hata oluştu");
            }
        },
        addText() {
            this.texts.push({ title: "", text: "" });
        },
        removeText(index) {
            this.texts.splice(index, 1);
        },
        beforeUpload(file) {
            // Otomatik yüklemeyi engelle ve dosyayı fileList'e ekle
            return false;
        },
        handleCreateFileChange(info) {
            this.createFileList = info.fileList;
        },
        handleCreateSubmit(e) {
            e.preventDefault();
            this.form.validateFields(async (err, values) => {
                if (!err) {
                    this.submitting = true;

                    try {
                        // URL'leri satır bazında ayır
                        const urls = values.knowledge_base_urls ?
                            values.knowledge_base_urls.split("\n").filter(url => url.trim()) :
                            [];

                        // Geçerli metinleri filtrele
                        const validTexts = this.texts.filter(t => t.title && t.text);

                        // Dosyaları hazırla
                        const files = this.createFileList.map(file => file.originFileObj);

                        const data = {
                            knowledge_base_name: values.knowledge_base_name,
                            knowledge_base_urls: urls,
                            knowledge_base_texts: validTexts,
                            knowledge_base_files: files,
                            enable_auto_refresh: values.enable_auto_refresh,
                        };

                        await VOICE_AGENT_API.knowledgeBases.create(data);
                        this.$message.success("Bilgi tabanı başarıyla oluşturuldu");
                        this.showCreateModal = false;
                        this.form.resetFields();
                        this.texts = [];
                        this.createFileList = [];
                        this.fetchKnowledgeBases();
                    } catch (error) {
                        console.error("Bilgi tabanı oluşturulurken hata oluştu:", error);
                        this.$message.error("Bilgi tabanı oluşturulurken bir hata oluştu");
                    } finally {
                        this.submitting = false;
                    }
                }
            });
        },
    },
    created() {
        this.fetchKnowledgeBases();
    },
};
</script>

<style scoped lang="scss">
.voice-agent-page-content {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 0 20px;

    .header-section {
        margin-bottom: 5px;
    }

    .title-wrapper {
        display: flex;
        align-items: center;
        gap: 15px;
        margin-bottom: 20px;
    }

    .main-title {
        font-size: 24px;
        font-weight: 500;
        margin: 0;
    }

    .title-description {
        font-size: 14px;
        font-weight: 400;
        color: #8c8c8c;
        margin: 0;
    }

    .section {
        display: flex;
        flex-direction: column;
        gap: 15px;
    }

    .table-wrapper {
        display: flex;
        flex-direction: column;
        gap: 10px;

        .toolbar {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px 0;
            gap: 5px;

            .left {
                display: flex;
                gap: 5px;
                align-items: center;
            }

            .right {
                display: flex;
                gap: 5px;
            }
        }
    }

    #knowledge-bases-table {
        .actions {
            display: flex;
            gap: 5px;
        }

        .kb-name-link {
            color: inherit;
            text-decoration: none;
            cursor: pointer;

            &:hover {
                text-decoration: underline !important;
            }
        }
    }

    .button-container {
        display: flex;
        justify-content: flex-end;
        margin-top: 20px;
    }

    .text-item {
        margin-bottom: 10px;
    }
}
</style>
